// src/components/admin/pages/BooksLibrary.jsx
import { useEffect, useMemo, useState } from 'react';
import { Search, BookOpen, Star, Sparkles, Award, Pencil } from 'lucide-react';
import { booksLibraryApi, enrichmentApi } from '../../../services/adminApi';
import BookQuickActionModal from '../BookQuickActionModal';
import BookUpdateFieldsModal from '../BookUpdateFieldsModal';

const TERMINAL_JOB_STATUSES = new Set(['completed', 'completed_with_errors', 'failed']);

const OUTCOME_BADGE_STYLES = {
  enriched: 'bg-green-100 text-green-800 border border-green-200',
  tag_only: 'bg-amber-100 text-amber-800 border border-amber-200',
  no_match: 'bg-gray-100 text-gray-700 border border-gray-200',
  error: 'bg-red-100 text-red-800 border border-red-200',
  never: 'bg-white text-gray-600 border border-gray-300',
};

const normalizeEnrichmentState = (payload) => {
  if (!payload || payload.status === 'never_enriched') {
    return {
      outcome: 'never',
      enrichedAt: null,
      fieldsUpdated: [],
      error: null,
    };
  }

  return {
    outcome: payload.outcome || 'never',
    enrichedAt: payload.enriched_at || null,
    fieldsUpdated: Array.isArray(payload.fields_updated) ? payload.fields_updated : [],
    error: payload.error || null,
  };
};

const resolveDbId = (book) => {
  return book?.dbId ?? (book?._source === 'database' && /^\d+$/.test(String(book?.id)) ? parseInt(book.id, 10) : null);
};

const BooksLibrary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [quickActionModal, setQuickActionModal] = useState(null);
  const [bookEditModal, setBookEditModal] = useState(null);
  const [enrichmentByBookId, setEnrichmentByBookId] = useState({});
  const [bulkJobId] = useState(null);
  const [bulkJobStatus, setBulkJobStatus] = useState(null);

  const dbBackedBooks = useMemo(() => {
    return books
      .map((book) => ({ book, dbId: resolveDbId(book) }))
      .filter(({ dbId }) => Boolean(dbId));
  }, [books]);

  const refreshBookEnrichmentStates = async (targetBooks) => {
    const uniqueBookIds = [...new Set(
      targetBooks
        .map((entry) => resolveDbId(entry.book || entry))
        .filter(Boolean)
    )];

    if (uniqueBookIds.length === 0) return;

    const results = await Promise.all(
      uniqueBookIds.map(async (bookId) => {
        try {
          const payload = await enrichmentApi.getBookEnrichment(bookId);
          return [bookId, normalizeEnrichmentState(payload)];
        } catch (_error) {
          return [bookId, normalizeEnrichmentState({ status: 'never_enriched' })];
        }
      })
    );

    setEnrichmentByBookId((previous) => ({
      ...previous,
      ...Object.fromEntries(results),
    }));
  };

  const handleSearch = async (query) => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      setBooks([]);
      setHasSearched(false);
      setError(null);
      setSuccessMessage(null);
      return;
    }

    if (trimmedQuery.length < 3) {
      setBooks([]);
      setHasSearched(false);
      setError('Search query must be at least 3 characters long');
      setSuccessMessage(null);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await booksLibraryApi.searchBooks(trimmedQuery);
      if (result && Array.isArray(result.books)) {
        setBooks(result.books);
      } else {
        setBooks([]);
        console.warn('Unexpected search response format:', result);
      }
      setHasSearched(true);
    } catch (err) {
      console.error('Search failed:', err);
      let errorMessage = 'Search failed';
      if (err.message) {
        errorMessage = err.message.includes('Network') ? 'Network error - please try again' : err.message;
      }
      setError(errorMessage);
      setBooks([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInput = (e) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    refreshBookEnrichmentStates(dbBackedBooks.map(({ book }) => book)).catch((nextError) => {
      console.error('Failed to refresh enrichment states:', nextError);
    });
  }, [dbBackedBooks]);

  useEffect(() => {
    if (!bulkJobId) return undefined;

    let cancelled = false;
    let intervalId = null;

    const pollBulkJob = async () => {
      try {
        const nextStatus = await enrichmentApi.getEnrichmentJob(bulkJobId);
        if (cancelled) return;

        setBulkJobStatus(nextStatus);

        if (TERMINAL_JOB_STATUSES.has(nextStatus.status)) {
          if (intervalId) {
            clearInterval(intervalId);
          }
          await refreshBookEnrichmentStates(dbBackedBooks.map(({ book }) => book));
        }
      } catch (nextError) {
        if (cancelled) return;
        setError(nextError.message || 'Failed to refresh enrichment job');
      }
    };

    pollBulkJob();
    intervalId = setInterval(pollBulkJob, 3000);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [bulkJobId, dbBackedBooks]);

  const handleQuickAction = (book, action) => {
    const resolvedDbId = resolveDbId(book);

    if (!resolvedDbId) {
      setError(
        `"${book?.title || 'This book'}" is available in search results but is not yet a Bukito database record. ` +
        `Only DB-backed books can be added to Top Picks, New Releases, or Book of the Week from this flow.`
      );
      return;
    }

    setError(null);
    setQuickActionModal({
      book: {
        ...book,
        dbId: resolvedDbId,
      },
      action,
    });
  };

  const handleQuickActionSuccess = () => {
    setQuickActionModal(null);
  };

  const handleOpenBookEdit = (book) => {
    const resolvedDbId = resolveDbId(book);

    if (!resolvedDbId) {
      setError('Only database-backed books can be updated from this admin flow.');
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setBookEditModal({
      bookId: resolvedDbId,
    });
  };

  const handleBookEditSuccess = (updatedBook, message, options = {}) => {
    setBooks((previous) => previous.map((book) => {
      const resolvedDbId = resolveDbId(book);
      if (resolvedDbId !== updatedBook.id) {
        return book;
      }

      if (options.tagsOnly) {
        return {
          ...book,
          ai_tags_v2: updatedBook.ai_tags_v2 || null,
          ai_tags_display: updatedBook.ai_tags_display || null,
        };
      }

      return {
        ...book,
        illustrator: updatedBook.illustrator || null,
        coverUrl: updatedBook.cover_url || null,
        cover_url: updatedBook.cover_url || null,
        thumbnail: updatedBook.cover_url || null,
        pageCount: updatedBook.number_of_pages ?? null,
        pages: updatedBook.number_of_pages ?? null,
        readingAge: updatedBook.reading_age || null,
        reading_age: updatedBook.reading_age || null,
        publisher: updatedBook.publisher || null,
        publishedDate: updatedBook.publication_date || null,
        publicationDate: updatedBook.publication_date || null,
        description: updatedBook.description || null,
        ai_tags_v2: updatedBook.ai_tags_v2 || null,
        ai_tags_display: updatedBook.ai_tags_display || null,
      };
    }));

    setSuccessMessage(message || 'Book fields updated successfully.');
    if (options.closeModal !== false) {
      setBookEditModal(null);
    }
  };

  const isBulkJobActive = Boolean(bulkJobId && !bulkJobStatus) || (bulkJobStatus && !TERMINAL_JOB_STATUSES.has(bulkJobStatus.status));

  const bulkProcessed = bulkJobStatus?.processed ?? (
    (bulkJobStatus?.enriched || 0) +
    (bulkJobStatus?.tag_only || 0) +
    (bulkJobStatus?.no_match || 0) +
    (bulkJobStatus?.error || 0) +
    (bulkJobStatus?.skipped || 0)
  );

  const bulkProgress = bulkJobStatus?.total
    ? Math.round((bulkProcessed / bulkJobStatus.total) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Books Library</h1>
          <p className="text-text-secondary">Search and select books for admin features</p>
        </div>
      </div>

      {isBulkJobActive && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Bulk enrichment in progress</h2>
              <p className="text-sm text-text-secondary">This page refreshes the enrichment job every 3 seconds.</p>
            </div>
            {bulkJobStatus?.status && (
              <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-blue-700 border border-blue-200">
                {bulkJobStatus.status}
              </span>
            )}
          </div>

          <div className="mt-5">
            <div className="mb-2 flex items-center justify-between text-sm text-text-secondary">
              <span>Processed {bulkProcessed} of {bulkJobStatus?.total || 0} books</span>
              <span>{bulkProgress}%</span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-white/80">
              <div
                className="h-full rounded-full bg-primary-500 transition-all"
                style={{ width: `${bulkProgress}%` }}
              />
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-4">
            <div className="rounded-lg bg-white p-3 border border-blue-100">
              <div className="text-xs text-text-secondary">Enriched</div>
              <div className="mt-1 text-lg font-semibold text-green-700">{bulkJobStatus?.enriched || 0}</div>
            </div>
            <div className="rounded-lg bg-white p-3 border border-blue-100">
              <div className="text-xs text-text-secondary">Tag Only</div>
              <div className="mt-1 text-lg font-semibold text-amber-700">{bulkJobStatus?.tag_only || 0}</div>
            </div>
            <div className="rounded-lg bg-white p-3 border border-blue-100">
              <div className="text-xs text-text-secondary">No Match</div>
              <div className="mt-1 text-lg font-semibold text-gray-700">{bulkJobStatus?.no_match || 0}</div>
            </div>
            <div className="rounded-lg bg-white p-3 border border-blue-100">
              <div className="text-xs text-text-secondary">Errors</div>
              <div className="mt-1 text-lg font-semibold text-red-700">{bulkJobStatus?.error || 0}</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-bg-surface rounded-lg border border-gray-200 p-4">
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchInput}
              placeholder="Search by title, author, or ISBN..."
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            />
          </div>
          <p className="text-sm text-text-secondary">
            Search across database books and Google Books API. Minimum 3 characters required. Results are deduplicated and cached.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {loading && (
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Searching books...</p>
        </div>
      )}

      {!loading && hasSearched && (
        <div className="space-y-4">
          {books.length > 0 ? (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-text-secondary">Found {books.length} book{books.length !== 1 ? 's' : ''}</p>
              </div>
              {books.map((book, index) => {
                const dbId = resolveDbId(book);
                const isDbBacked = Boolean(dbId);
                const enrichmentState = isDbBacked
                  ? (enrichmentByBookId[dbId] || normalizeEnrichmentState({ status: 'never_enriched' }))
                  : null;
                const badgeLabel = enrichmentState?.outcome === 'tag_only'
                  ? 'tag_only'
                  : enrichmentState?.outcome || 'never';
                const badgeClassName = OUTCOME_BADGE_STYLES[badgeLabel] || OUTCOME_BADGE_STYLES.never;

                return (
                  <div key={book.id || index} className="bg-bg-surface rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-22 bg-gray-200 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {(book.coverUrl || book.thumbnail) ? (
                          <img src={book.coverUrl || book.thumbnail} alt={book.title} className="w-full h-full object-cover rounded" />
                        ) : (
                          <BookOpen className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                          <div>
                            <h3 className="font-semibold text-text-primary mb-1">{book.title || 'Unknown Title'}</h3>
                            <p className="text-text-secondary text-sm mb-2">
                              by {Array.isArray(book.authors) ? book.authors.join(', ') : book.authors || 'Unknown Author'}
                            </p>
                          </div>
                          {isDbBacked && (
                            <span className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-medium ${badgeClassName}`}>
                              {badgeLabel}
                            </span>
                          )}
                        </div>

                        <div className="flex space-x-4 text-xs text-text-secondary mb-3 flex-wrap gap-y-2">
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded">ID: {book.id}</span>
                          {(book.publishedYear || book.publishedDate) && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                              {book.publishedYear || new Date(book.publishedDate).getFullYear()}
                            </span>
                          )}
                          {(book.pages || book.pageCount) && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                              {book.pages || book.pageCount} pages
                            </span>
                          )}
                          {(book.rating || book.averageRating) && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                              ★ {book.rating || book.averageRating}
                            </span>
                          )}
                          {isDbBacked && enrichmentState?.enrichedAt && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                              Enriched {new Date(enrichmentState.enrichedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        <div className="flex space-x-2 flex-wrap gap-1">
                          {isDbBacked && (
                            <button
                              onClick={() => handleOpenBookEdit(book)}
                              className="flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary-700 text-xs rounded hover:bg-primary-100"
                            >
                              <Pencil className="w-3 h-3" />
                              <span>Update Fields</span>
                            </button>
                          )}
                          <button
                            onClick={() => handleQuickAction(book, 'top-picks')}
                            className="flex items-center space-x-1 px-3 py-1 bg-blue-50 text-blue-600 text-xs rounded hover:bg-blue-100"
                          >
                            <Star className="w-3 h-3" />
                            <span>Add to Top Picks</span>
                          </button>
                          <button
                            onClick={() => handleQuickAction(book, 'new-releases')}
                            className="flex items-center space-x-1 px-3 py-1 bg-green-50 text-green-600 text-xs rounded hover:bg-green-100"
                          >
                            <Sparkles className="w-3 h-3" />
                            <span>Add to New Releases</span>
                          </button>
                          <button
                            onClick={() => handleQuickAction(book, 'book-of-week')}
                            className="flex items-center space-x-1 px-3 py-1 bg-yellow-50 text-yellow-600 text-xs rounded hover:bg-yellow-100"
                          >
                            <Award className="w-3 h-3" />
                            <span>Set as Book of Week</span>
                          </button>
                        </div>

                        {isDbBacked && enrichmentState?.error && (
                          <p className="mt-3 text-xs text-red-600">{enrichmentState.error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="bg-bg-surface rounded-lg border border-gray-200 p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-text-primary mb-2">No books found</h3>
              <p className="text-text-secondary">Try a different search term or check your spelling.</p>
            </div>
          )}
        </div>
      )}

      {!loading && !hasSearched && (
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-8 text-center text-text-secondary">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-text-primary mb-2">Search Books</h3>
          <p className="mb-4">
            Search across database books and Google Books API to find books for admin features.
          </p>
          <div className="text-sm text-text-secondary space-y-1">
            <p>• Search by title, author, or ISBN (minimum 3 characters)</p>
            <p>• Add books to Top Picks, New Releases, or Book of the Week</p>
            <p>• Review database-backed results together with their current enrichment status</p>
          </div>
        </div>
      )}

      {quickActionModal && (
        <BookQuickActionModal
          isOpen={true}
          book={quickActionModal.book}
          action={quickActionModal.action}
          onClose={() => setQuickActionModal(null)}
          onSuccess={handleQuickActionSuccess}
        />
      )}

      {bookEditModal && (
        <BookUpdateFieldsModal
          isOpen={true}
          bookId={bookEditModal.bookId}
          onClose={() => setBookEditModal(null)}
          onSuccess={handleBookEditSuccess}
        />
      )}
    </div>
  );
};

export default BooksLibrary;
